import React, { useMemo } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faHome } from '@fortawesome/free-solid-svg-icons';
import { KbFolder } from '../types';

interface FolderOption {
  id: number | null;
  name: string;
  path: string;
  depth: number;
}

const flattenFolders = (folders: KbFolder[], depth = 0, parentPath = ''): FolderOption[] => {
  const result: FolderOption[] = [];
  for (const folder of folders) {
    const path = parentPath ? `${parentPath} / ${folder.name}` : folder.name;
    result.push({ id: folder.id, name: folder.name, path, depth });
    result.push(...flattenFolders(folder.children, depth + 1, path));
  }
  return result;
};

interface Props {
  folders: KbFolder[];
  value: number | null;
  onChange: (folderId: number | null) => void;
  label?: string;
}

const ROOT_OPTION: FolderOption = { id: null, name: 'Root', path: 'Root', depth: 0 };

const FolderSelect: React.FC<Props> = ({ folders, value, onChange, label = 'Parent folder' }) => {
  const options = useMemo(() => [ROOT_OPTION, ...flattenFolders(folders)], [folders]);

  const selected = options.find((o) => o.id === value) || ROOT_OPTION;

  return (
    <Autocomplete
      value={selected}
      onChange={(_, newValue) => onChange(newValue ? newValue.id : null)}
      options={options}
      getOptionLabel={(opt) => opt.path}
      filterOptions={(opts, { inputValue }) => {
        if (!inputValue) return opts;
        const lower = inputValue.toLowerCase();
        return opts.filter((o) => o.name.toLowerCase().includes(lower) || o.path.toLowerCase().includes(lower));
      }}
      renderOption={(opt) => (
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: opt.id === null ? 0 : opt.depth * 16 }}>
          <FontAwesomeIcon
            icon={opt.id === null ? faHome : faFolder}
            style={{ color: opt.id === null ? '#888' : '#f9a825', marginRight: 8, fontSize: 13 }}
          />
          <span>
            {opt.id === null ? (
              <em style={{ color: '#888' }}>Root (no parent)</em>
            ) : (
              <>
                {opt.depth > 0 && (
                  <span style={{ color: '#aaa', fontSize: 12, marginRight: 4 }}>
                    {'└ '.repeat(1)}
                  </span>
                )}
                <span>{opt.name}</span>
                {opt.depth > 0 && (
                  <span style={{ color: '#aaa', fontSize: 11, marginLeft: 6 }}>
                    {opt.path}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      )}
      renderInput={(params) => <TextField {...params} label={label} margin="dense" fullWidth />}
      disableClearable
      size="small"
    />
  );
};

export default FolderSelect;
